import React, { useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  IconButton,
  Snackbar,
  Alert,
} from "@mui/material";
import { Add as AddIcon, Remove as RemoveIcon } from "@mui/icons-material";
import { adjustWallet } from "../../services/dataService";
import { logUserActivity } from "../../utils/activityLogger";
import {
  validateAdjustment,
  formatPreview,
  MAX_REASON,
} from "../../utils/adjustmentRules";

const actionButtonSx = { padding: "8px 32px", fontSize: 15, fontWeight: 500 };
const STEP = 0.5;

// Shared by Students.js and Tutors.js (spec 026-phase2 Slice W, task W2):
// one dialog, opened from either drawer's "Adjust balance" button. `person`
// is whatever the drawer currently has selected — needs only `id`, `name`
// and `WalletBalance` (missing balance previews/logs as 0, same as the rest
// of the drawer). `onAdjusted` is the caller's own refetch (e.g. re-running
// its openDrawer) so the drawer's balance — and transactions list, where one
// exists — picks up the new ledger row.
const AdjustBalanceDialog = ({ open, onClose, person, actingUser, onAdjusted }) => {
  const [delta, setDelta] = useState(0);
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const notify = (message, severity = "success") =>
    setSnackbar({ open: true, message, severity });
  const closeSnackbar = () => setSnackbar((s) => ({ ...s, open: false }));

  const currentBalance = Number(person?.WalletBalance) || 0;
  const { valid, error: validationError } = validateAdjustment({ delta, reason });
  const trimmedReasonLength = reason.trim().length;

  const reset = () => {
    setDelta(0);
    setReason("");
  };

  const handleClose = () => {
    if (submitting) return;
    reset();
    onClose();
  };

  const step = (amount) =>
    setDelta((d) => Math.round((Number(d) + amount) * 2) / 2);

  const submit = async () => {
    if (!valid || !person) return;
    setSubmitting(true);
    const numDelta = Number(delta);
    try {
      const result = await adjustWallet({
        userId: person.id,
        delta: numDelta,
        reason: reason.trim(),
      });
      // The server's balanceBefore/balanceAfter are authoritative — the
      // locally-held currentBalance is only what the drawer had loaded, and
      // can have drifted if the balance moved since then. Fall back to the
      // local numbers only if the callable ever omits them.
      const balanceBefore = result?.balanceBefore ?? currentBalance;
      const balanceAfter = result?.balanceAfter ?? currentBalance + numDelta;
      await logUserActivity({
        userId: actingUser?.uid || actingUser?.id || "unknown",
        userName: actingUser?.name || actingUser?.displayName || "Unknown User",
        userEmail: actingUser?.email || "unknown@example.com",
        // UPPER_SNAKE: the Activity Log only prettifies actions in that shape.
        action: "WALLET_ADJUST",
        entityType: "user",
        entityId: person.id,
        entityName: person.name,
        before: { WalletBalance: balanceBefore },
        after: { WalletBalance: balanceAfter, delta: numDelta },
        description: `${actingUser?.name || "User"} adjusted ${person.name}'s balance by ${
          numDelta > 0 ? "+" : ""
        }${numDelta} sessions`,
      });
      notify(`Adjusted ${person.name}'s balance.`);
      reset();
      onClose();
      if (onAdjusted) await onAdjusted();
    } catch (e) {
      notify(e.message || "Failed to adjust balance.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: "8px" } }}
      >
        <DialogTitle style={{ fontWeight: 600 }}>
          Adjust balance{person?.name ? ` — ${person.name}` : ""}
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
            <IconButton onClick={() => step(-STEP)} disabled={submitting} size="small">
              <RemoveIcon />
            </IconButton>
            <TextField
              label="Adjustment (sessions)"
              type="number"
              value={delta}
              onChange={(e) => setDelta(e.target.value === "" ? "" : Number(e.target.value))}
              inputProps={{ step: STEP }}
              disabled={submitting}
              sx={{ width: 200 }}
            />
            <IconButton onClick={() => step(STEP)} disabled={submitting} size="small">
              <AddIcon />
            </IconButton>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            {formatPreview({ currentBalance, delta })}
          </Typography>
          <TextField
            label="Reason"
            fullWidth
            multiline
            rows={3}
            margin="normal"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            disabled={submitting}
            helperText={`${trimmedReasonLength}/${MAX_REASON}`}
            error={trimmedReasonLength > MAX_REASON}
          />
          {!valid && (delta !== 0 || trimmedReasonLength > 0) && (
            <Typography variant="caption" color="error">
              {validationError}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={submitting} sx={actionButtonSx}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={!valid || submitting}
            onClick={submit}
            sx={actionButtonSx}
          >
            {submitting ? "Saving..." : "Adjust"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={closeSnackbar}>
        <Alert severity={snackbar.severity} onClose={closeSnackbar}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default AdjustBalanceDialog;
